import * as Phaser from "phaser"
import { GameState } from "../core/game-state"
import { InputManager } from "../core/input-mapper"
import { SCENE } from "../core/keys"
import { type LeaderboardEntry, PersistedData } from "../core/persisted-data"
import { THEME } from "../core/theme"
import { playClick } from "../core/ui-sfx"
import {
  DEFEAT_MESSAGES,
  GAME_HEIGHT,
  GAME_WIDTH,
  MAX_WAVES,
  VICTORY_MESSAGES,
} from "../data/constants"
import type { Player } from "../types"
import { NeonPanel, RetroButton } from "../ui/components"
import {
  NEON,
  drawCornerAccents,
  drawNeonLine,
  drawScanlines,
  neonTextShadow,
} from "../ui/neon-fx"

const FONT = THEME.fonts
const COLOR = THEME.colors

interface GameOverData {
  isVictory: boolean
  score: number
  wave: number
  coins: number
  totalBumperHits: number
  player: Player
}

export class GameOverScene extends Phaser.Scene {
  private isVictory = false
  private score = 0
  private wave = 1
  private coins = 0
  private totalBumperHits = 0
  private player!: Player
  private inputManager!: InputManager

  constructor() {
    super({ key: SCENE.GAME_OVER })
  }

  init(data: GameOverData): void {
    this.isVictory = data.isVictory ?? false
    this.score = data.score ?? 0
    this.wave = data.wave ?? 1
    this.coins = data.coins ?? 0
    this.totalBumperHits = data.totalBumperHits ?? 0
    this.player =
      data.player ??
      ({
        level: 1,
        hp: 0,
        maxHp: 100,
      } as unknown as Player)
  }

  create(): void {
    this.inputManager = new InputManager(this, {
      onDown: (action, event) => {
        // Ignore global pointer events for these actions in the menu
        if (event?.pointer) return

        if (
          action === "CONFIRM" ||
          action === "FLAP" ||
          action === "REROLL" ||
          action === "BACK"
        ) {
          this.returnToMenu()
        }
      },
    })

    const previousBestScore = PersistedData.getHighScores()[0] ?? 0
    const previousBestWave = PersistedData.getBestWaveReached()

    // Save high score
    PersistedData.addHighScore(this.score)

    // Track best wave reached
    PersistedData.setBestWaveReached(this.wave)
    const isNewHighScore = this.score > previousBestScore
    const isNewBestWave = this.wave > previousBestWave

    // Save leaderboard entry
    const heroId = GameState.instance.character?.id ?? "bella"
    const dangerLevel = PersistedData.getDangerLevel()
    const entry: LeaderboardEntry = {
      score: this.score,
      hero: heroId,
      wavesReached: this.wave,
      dangerLevel,
      date: new Date().toISOString().slice(0, 10),
    }
    PersistedData.addLeaderboardEntry(entry)

    // Danger level progression: clearing wave 20 unlocks next danger level
    if (this.isVictory && this.wave >= MAX_WAVES) {
      const maxDanger = PersistedData.getMaxDangerUnlocked()
      if (dangerLevel >= maxDanger && maxDanger < 5) {
        PersistedData.setMaxDangerUnlocked(maxDanger + 1)
      }
    }

    const cx = GAME_WIDTH / 2
    const cy = GAME_HEIGHT / 2

    // Background with fade-in
    const overlay = this.add.rectangle(
      cx,
      cy,
      GAME_WIDTH,
      GAME_HEIGHT,
      COLOR.inkAlt,
      0,
    )
    this.tweens.add({
      targets: overlay,
      alpha: 0.95,
      duration: 500,
      ease: "Quad.easeOut",
    })

    // Content container for fade-in animation
    const content = this.add.container(0, 0).setAlpha(0)
    this.tweens.add({
      targets: content,
      alpha: 1,
      duration: 800,
      delay: 300,
      ease: "Quad.easeOut",
    })

    // Scanline overlay
    const scanlines = drawScanlines(
      this,
      0,
      0,
      GAME_WIDTH,
      GAME_HEIGHT,
      0.03,
      3,
    )
    scanlines.setDepth(10).setAlpha(0)
    this.tweens.add({ targets: scanlines, alpha: 1, duration: 800, delay: 300 })

    // ── Title with neon glow ────────────────────────────────────
    const titleColor = this.isVictory ? COLOR.successText : COLOR.dangerText
    const titleNeon = this.isVictory
      ? THEME.shadows.green
      : THEME.shadows.redStrong
    const titleText = this.isVictory ? "EARTH IS SAVED!" : "GAME OVER"

    content.add(
      this.add
        .text(cx, cy - 120, titleText, {
          fontSize: "42px",
          fontFamily: FONT.sans,
          fontStyle: "900",
          color: titleColor,
          shadow: neonTextShadow(titleNeon, 16),
        })
        .setOrigin(0.5),
    )

    // Neon accent line under title
    const titleAccent = this.add.graphics()
    const accentColor = this.isVictory ? NEON.green : NEON.red
    drawNeonLine(
      titleAccent,
      cx - 120,
      cy - 94,
      cx + 120,
      accentColor,
      0.4,
      0.1,
      1,
      4,
    )
    content.add(titleAccent)

    // ── Random message ──────────────────────────────────────────
    const messages = this.isVictory ? VICTORY_MESSAGES : DEFEAT_MESSAGES
    const message = messages[Math.floor(Math.random() * messages.length)]

    content.add(
      this.add
        .text(cx, cy - 76, message, {
          fontSize: "13px",
          fontFamily: FONT.sans,
          fontStyle: "500",
          color: COLOR.whiteText,
          wordWrap: { width: 400 },
          align: "center",
          lineSpacing: 4,
        })
        .setOrigin(0.5)
        .setAlpha(0.7),
    )

    if (isNewHighScore || isNewBestWave) {
      const recordLabel = [
        isNewHighScore ? "NEW HIGH SCORE" : null,
        isNewBestWave ? "NEW BEST WAVE" : null,
      ]
        .filter(Boolean)
        .join(" • ")
      const recordText = this.add
        .text(cx, cy - 50, recordLabel, {
          fontSize: "11px",
          fontFamily: FONT.sans,
          fontStyle: "900",
          color: COLOR.warningText,
          letterSpacing: 3,
          shadow: neonTextShadow(THEME.shadows.gold, 10),
        })
        .setOrigin(0.5)
      content.add(recordText)
      this.tweens.add({
        targets: recordText,
        alpha: { from: 0.55, to: 1 },
        duration: 520,
        yoyo: true,
        repeat: -1,
      })
    }

    // ── Stats panel ─────────────────────────────────────────────
    const panelW = 280
    const panelH = 160
    const panelX = cx - panelW / 2
    const panelY = cy - 36
    const panelNeonColor = this.isVictory ? NEON.cyan : NEON.magenta

    const panel = new NeonPanel(this, {
      x: panelX,
      y: panelY,
      width: panelW,
      height: panelH,
      color: panelNeonColor,
      radius: 14,
      glowStrength: 6,
    })
    content.add(panel)
    drawCornerAccents(
      panel.getBorderGraphics(),
      4,
      4,
      panelW - 8,
      panelH - 8,
      panelNeonColor,
      10,
      0.3,
    )

    // Stats grid (2x2)
    const stats = [
      {
        label: "SCORE",
        value: this.score.toLocaleString(),
        x: panelW * 0.25,
        y: 30,
      },
      {
        label: "WAVE",
        value: `${this.wave}/${MAX_WAVES}`,
        x: panelW * 0.75,
        y: 30,
      },
      {
        label: "LEVEL",
        value: `${this.player.level}`,
        x: panelW * 0.25,
        y: 90,
      },
      { label: "COIN", value: `${this.coins}`, x: panelW * 0.75, y: 90 },
    ]

    for (const stat of stats) {
      panel.add(
        this.add
          .text(stat.x, stat.y - 10, stat.label, {
            fontSize: "9px",
            fontFamily: FONT.sans,
            fontStyle: "700",
            color: COLOR.cyanText,
            letterSpacing: 2,
            shadow: neonTextShadow(THEME.shadows.cyan, 4),
          })
          .setOrigin(0.5)
          .setAlpha(0.5),
      )

      panel.add(
        this.add
          .text(stat.x, stat.y + 8, stat.value, {
            fontSize: "22px",
            fontFamily: FONT.sans,
            fontStyle: "900",
            color: COLOR.whiteText,
            shadow: neonTextShadow(THEME.shadows.white, 4),
          })
          .setOrigin(0.5),
      )
    }

    // Separator
    const sep = this.add.graphics()
    sep.fillStyle(COLOR.white, 0.05)
    sep.fillRect(20, 68, panelW - 40, 1)
    panel.add(sep)

    const bestScore = PersistedData.getHighScores()[0] ?? this.score
    content.add(
      this.add
        .text(
          cx,
          panelY + panelH + 12,
          `BUMPER HITS ${this.totalBumperHits.toLocaleString()} • BEST SCORE ${bestScore.toLocaleString()}`,
          {
            fontSize: "10px",
            fontFamily: FONT.sans,
            fontStyle: "900",
            color: COLOR.warningText,
            letterSpacing: 2,
            shadow: neonTextShadow(THEME.shadows.goldSoft, 6),
          },
        )
        .setOrigin(0.5)
        .setAlpha(0.85),
    )

    const funMessage = this.isVictory
      ? "Humanity is grateful! The void has been cleared."
      : "You failed like a human."

    content.add(
      this.add
        .text(cx, panelY + panelH + 28, funMessage, {
          fontSize: "10px",
          fontFamily: FONT.sans,
          fontStyle: "500",
          color: COLOR.whiteText,
        })
        .setOrigin(0.5)
        .setAlpha(0.4),
    )

    // ── MAIN MENU button ────────────────────────────────────────
    const btnW = 120
    const btnH = 32
    const menuBtnY = panelY + panelH + 56
    const mainMenuBtn = new RetroButton(this, {
      inputManager: this.inputManager,
      x: cx - btnW / 2,
      y: menuBtnY,
      width: btnW,
      height: btnH,
      text: "MAIN MENU",
      color: 0x2d4578,
      fontSize: "8px",
      onClick: () => this.returnToMenu(),
    })
    content.add(mainMenuBtn)

    // ── Leaderboard (compact, bottom-right) ────────────────────
    const board = PersistedData.getLeaderboard()
    if (board.length > 0) {
      const lbX = GAME_WIDTH - 160
      const lbY = 20
      content.add(
        this.add
          .text(lbX, lbY, "TOP SCORES", {
            fontSize: "8px",
            fontFamily: "sans-serif",
            fontStyle: "900",
            color: COLOR.cyanText,
            letterSpacing: 2,
            shadow: neonTextShadow(THEME.shadows.cyan, 4),
          })
          .setAlpha(0.5),
      )

      board.slice(0, 5).forEach((e, idx) => {
        const y = lbY + 16 + idx * 14
        const isCurrent = e.score === this.score && e.hero === heroId
        const color = isCurrent ? COLOR.warningText : COLOR.mutedTextBlue
        content.add(
          this.add
            .text(lbX, y, `${idx + 1}. ${e.score.toLocaleString()}`, {
              fontSize: "9px",
              fontFamily: FONT.sans,
              fontStyle: "700",
              color,
            })
            .setAlpha(isCurrent ? 1 : 0.6),
        )
        content.add(
          this.add
            .text(lbX + 100, y, `W${e.wavesReached}`, {
              fontSize: "8px",
              fontFamily: FONT.sans,
              fontStyle: "500",
              color: COLOR.disabledText,
            })
            .setAlpha(0.5),
        )
      })
    }

    // Cleanup
    this.events.once("shutdown", () => {
      this.tweens.killAll()
      this.inputManager.destroy()
    })
  }

  private returnToMenu(): void {
    playClick(this)
    this.scene.stop(SCENE.WAVE)
    this.scene.start(SCENE.MENU)
  }
}
